import { type BankDetailsModel } from '@acme/api';
import { Page } from '@acme/foundation';
import {
  $application,
  $contract,
  $isLoading,
  applicationOpened,
  applicationReset,
} from '@entities/application';

import { actionSucceeded } from '@features/applicationActions';
import {
  ApplicationForm,
  type ApplicationFormSuccessResult,
} from '@widgets/applicationForm';
import type { ApplicationFormationFormCtx } from '@widgets/applicationForm/model/formConfig';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditApplicationActionHandlers } from '../lib/actionsHandlers';
import { getCreateApplicationBreadcrumbs } from '../lib/breadcrumbs';
import {
  $result,
  createApplicationClient,
  createSucceeded,
  formOpened,
  resetResult,
} from '../model';
import { ResultScreen } from './resultScreen';

export const ApplicationFormation = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    application,
    contract,
    isApplicationLoading,
    result,
    onApplicationOpened,
    onApplicationReset,
    onFormOpened,
    onResetResult,
    onCreateSucceeded,
  } = useUnit({
    application: $application,
    contract: $contract,
    isApplicationLoading: $isLoading,
    result: $result,
    onApplicationOpened: applicationOpened,
    onApplicationReset: applicationReset,
    onFormOpened: formOpened,
    onResetResult: resetResult,
    onCreateSucceeded: createSucceeded,
  });

  useEffect(() => {
    const unsub = actionSucceeded.watch(() => navigate('/dashboard'));
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    onResetResult();
    onFormOpened();
  }, [onResetResult, onFormOpened]);

  const handleSuccess = useCallback(
    (payload: ApplicationFormSuccessResult) => {
      onCreateSucceeded(payload);
    },
    [onCreateSucceeded]
  );

  const breadcrumbItems = useMemo(
    () =>
      getCreateApplicationBreadcrumbs(
        navigate,
        id ? (application?.name ?? null) : null
      ),
    [navigate, id, application?.name]
  );

  useEffect(() => {
    if (!id) return;
    onApplicationOpened({ id });
    return () => onApplicationReset();
  }, [id, onApplicationOpened, onApplicationReset]);

  useEffect(() => {
    if (!result?.applicationId) return;
    onApplicationOpened({ id: result.applicationId });
  }, [result?.applicationId, onApplicationOpened]);

  const editDefaultData: ApplicationFormationFormCtx | undefined =
    useMemo(() => {
      if (!id || !application) return undefined;

      const bankDetails: BankDetailsModel | undefined = contract?.country?.id
        ? {
            destinationCountry: contract.country.id,
            bankName: contract.bankName ?? null,
            bankAddress: contract.bankAddress ?? null,
            iban: contract.iban ?? null,
            swift: contract.swifT_BIC ?? null,
          }
        : undefined;

      return {
        applicationType: application.processType?.id,
        name: application.name ?? null,
        counterparty: contract?.counterparty ?? null,
        dealSubject: contract?.subjectTransaction ?? null,
        dealAmount: contract?.amount ?? undefined,
        transferCurrency: contract?.currency?.id,
        bankDetails,
      };
    }, [id, application, contract]);

  const defaultData = id ? editDefaultData : undefined;

  const actionHandlers = useEditApplicationActionHandlers();
  if (result) {
    return (
      <Page breadcrumbItems={breadcrumbItems} isLoading={isApplicationLoading}>
        <div className="flex flex-col gap-6">
          <ResultScreen
            result={result}
            onBack={() => onResetResult()}
            onHome={() => navigate('/dashboard')}
          />
        </div>
      </Page>
    );
  }

  return (
    <Page breadcrumbItems={breadcrumbItems} isLoading={isApplicationLoading}>
      <ApplicationForm
        defaultData={defaultData}
        application={application}
        createApplication={createApplicationClient}
        onSuccess={handleSuccess}
        actionHandlers={actionHandlers}
      />
    </Page>
  );
};
